import {
    Badge,
    Box,
    Button,
    Container,
    Dialog,
    Field,
    Flex,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    Portal,
    Progress,
    Separator,
    SimpleGrid,
    Spinner,
    Stack,
    Text,
} from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { toaster } from '../components/ui/toaster'
import { LuEye, LuEyeOff, LuFilm, LuLink, LuMaximize2, LuPause, LuPencil, LuPlay, LuStar, LuTrash, LuUpload } from 'react-icons/lu'
import { QRCodeCanvas } from 'qrcode.react'
import { EmptyState } from '../components/ui/EmptyState'
import { DownloadButton } from '../components/ui/DownloadButton'
import { Tooltip } from '../components/ui/tooltip'
import { toggleReelFavorite, toggleReelDisabled, sharedReelUrl } from '../services/firebaseService'
import { StreamPlayer } from '../components/ui/StreamPlayer'
import { isStreamReady } from '../utils/streamCopy'
import { useDocumentData, useCollection } from '@kage1020/react-firebase-hooks/firestore'
import { doc, collection, query, where, orderBy } from 'firebase/firestore'
import { db } from '../firebase'
import { uploadReel, replaceReelFile, updateReelTitle, updateReelThumbTime, deleteReel, backfillExistingReels, retryStreamUpload } from '../services/firebaseService'
import { ThumbnailPicker } from '../components/reels/ThumbnailPicker'
import { thumbnailAt, thumbTimeOf } from '../utils/thumbnails'
import { useTranslation } from '../i18n'
import { titleFromFileName } from '../utils/reelTitle'




const ReelCard = ({ reel, onOpenModal, onDelete, onShare, onEdit }) => {
    const { t } = useTranslation()
    const videoRef = useRef(null)
    const [isPlaying, setIsPlaying] = useState(false)

    // A disabled reel is gone from the gallery and its share link says so, but
    // it stays here in full: the point of disabling rather than deleting is
    // that everything is still there to put back. Every control below keeps
    // working -- except sharing, which would only hand out that message.
    const isDisabled = reel.disabled === true

    const togglePlay = () => {
        const v = videoRef.current
        if (!v) return
        if (v.paused) v.play()
        else v.pause()
    }

    const formatTime = (seconds) => {
        if (!seconds) return ''
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`
    }

    const formatDate = (timestamp) => {
        if (!timestamp) return ''
        return new Date(timestamp.seconds * 1000).toLocaleString()
    }

    const formatSize = (bytes) => {
        if (!bytes) return ''
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const handleDelete = (e) => {
        e.stopPropagation()
        if (!window.confirm(t('reels.deleteConfirm', { name: reel.title || reel.fileName }))) return
        onDelete(reel.id, reel.storagePath)
    }

    return (
        <Box
            borderWidth="1px"
            borderRadius="lg"
            overflow="hidden"
            bg="bg.surface"
            borderColor="border.subtle"
            cursor="pointer"
            onClick={() => onOpenModal(reel)}
            _hover={{ borderColor: 'border.emphasized' }}
            transition="border-color 0.15s"
        >
            {/* The media area plays in place; only the rest of the card opens the viewer. */}
            <Box
                position="relative"
                width="100%"
                height="200px"
                bg="black"
                cursor="default"
                onClick={(e) => e.stopPropagation()}
            >
                {isDisabled && (
                    <Badge
                        position="absolute"
                        top={1}
                        left={1}
                        zIndex={1}
                        colorPalette="orange"
                        size="sm"
                    >
                        {t('reels.disabledBadge')}
                    </Badge>
                )}
                {isStreamReady(reel) ? (
                    <StreamPlayer
                        cf={reel.cf}
                        poster={thumbnailAt(reel.cf?.thumbnailUrl, reel.thumbTime)}
                        controls
                        fill
                    />
                ) : (
                    <>
                        <video
                            ref={videoRef}
                            src={reel.videoUrl}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' }}
                            preload="metadata"
                            onClick={togglePlay}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                        />
                        <Flex
                            position="absolute"
                            inset={0}
                            align="center"
                            justify="center"
                            opacity={isPlaying ? 0 : 1}
                            transition="opacity 0.2s"
                            bg="blackAlpha.400"
                            pointerEvents="none"
                        >
                            <Icon as={LuPlay} boxSize={8} color="white" />
                        </Flex>
                    </>
                )}
                {reel.duration && (
                    <Flex position="absolute" bottom={1} right={1} bg="blackAlpha.700" px={1} borderRadius="sm">
                        <Text fontSize="xs" color="white">{formatTime(reel.duration)}</Text>
                    </Flex>
                )}
            </Box>

            <Box p={3} opacity={isDisabled ? 0.6 : 1}>
                <Text fontSize="sm" fontWeight="medium" noOfLines={1} mb={1}>
                    {reel.title || reel.fileName}
                </Text>
                <Text fontSize="xs" color="fg.muted" mb={2}>
                    {formatDate(reel.createdAt)}{reel.fileSize ? ` · ${formatSize(reel.fileSize)}` : ''}
                </Text>
                {reel.cf?.status === 'processing' && (
                    <Text fontSize="xs" color="fg.muted" mb={2}>{t('reels.streamProcessing')}</Text>
                )}
                {reel.cf?.status === 'error' && (
                    <HStack gap={2} mb={2}>
                        <Text fontSize="xs" color="red.400">{t('reels.streamError')}</Text>
                        <Button size="xs" variant="outline" onClick={async (e) => {
                            e.stopPropagation()
                            try {
                                await retryStreamUpload({ kind: 'reel', id: reel.id })
                            } catch (err) {
                                toaster.create({ title: t('reels.streamError'), description: err.message, status: 'error' })
                            }
                        }}>
                            {t('reels.streamRetry')}
                        </Button>
                    </HStack>
                )}
                <HStack justify="flex-end">
                    {!isStreamReady(reel) && (
                        <Tooltip content={isPlaying ? t('reels.pause') : t('reels.play')} openDelay={300} closeDelay={100}>
                            <IconButton
                                size="sm"
                                variant="ghost"
                                aria-label={t('reels.playPause')}
                                onClick={(e) => { e.stopPropagation(); togglePlay() }}
                            >
                                {isPlaying ? <LuPause /> : <LuPlay />}
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip
                        content={
                            <Stack gap="0.5">
                                <Text>{reel.isFavorite ? t('reels.unfavorite') : t('reels.favorite')}</Text>
                                <Text fontSize="xs" opacity={0.75}>{t('reels.favoriteNote')}</Text>
                            </Stack>
                        }
                        openDelay={300}
                        closeDelay={100}
                    >
                        <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label={t('reels.favorite')}
                            color={reel.isFavorite ? 'yellow.400' : 'inherit'}
                            onClick={async (e) => {
                                e.stopPropagation()
                                await toggleReelFavorite(reel.id, !reel.isFavorite)
                            }}
                        >
                            <LuStar fill={reel.isFavorite ? 'currentColor' : 'none'} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        content={
                            <Stack gap="0.5">
                                <Text>{isDisabled ? t('reels.enable') : t('reels.disable')}</Text>
                                <Text fontSize="xs" opacity={0.75}>{t('reels.disableNote')}</Text>
                            </Stack>
                        }
                        openDelay={300}
                        closeDelay={100}
                    >
                        <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label={isDisabled ? t('reels.enable') : t('reels.disable')}
                            color={isDisabled ? 'orange.400' : 'inherit'}
                            onClick={async (e) => {
                                e.stopPropagation()
                                await toggleReelDisabled(reel.id, !isDisabled)
                            }}
                        >
                            {isDisabled ? <LuEyeOff /> : <LuEye />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip content={t('reels.openViewer')} openDelay={300} closeDelay={100}>
                        <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label={t('reels.expand')}
                            onClick={(e) => { e.stopPropagation(); onOpenModal(reel) }}
                        >
                            <LuMaximize2 />
                        </IconButton>
                    </Tooltip>
                    {/* Shared links play the Stream copy only, so there is
                        nothing to share until the encode finishes -- and a
                        disabled reel's link only shows the unavailable
                        message, so handing one out is worse than saying why. */}
                    <Tooltip
                        content={
                            isDisabled ? t('reels.shareDisabled')
                                : isStreamReady(reel) ? t('reels.shareLink')
                                    : t('reels.shareNotReady')
                        }
                        openDelay={300}
                        closeDelay={100}
                    >
                        <span>
                            <IconButton
                                size="sm"
                                variant="ghost"
                                aria-label={t('reels.share')}
                                disabled={isDisabled || !isStreamReady(reel)}
                                onClick={(e) => { e.stopPropagation(); onShare(reel) }}
                            >
                                <LuLink />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <DownloadButton
                        iconOnly
                        size="sm"
                        variant="ghost"
                        source={isStreamReady(reel) ? { kind: 'reel', id: reel.id } : undefined}
                        url={reel.videoUrl}
                        fileName={reel.fileName || `reel-${reel.id}.mp4`}
                        tooltip={t('reels.downloadVideo')}
                    />
                    <Tooltip content={t('reels.editReel')} openDelay={300} closeDelay={100}>
                        <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label={t('reels.edit')}
                            onClick={(e) => { e.stopPropagation(); onEdit(reel) }}
                        >
                            <LuPencil />
                        </IconButton>
                    </Tooltip>
                    <Tooltip content={t('reels.deleteReel')} openDelay={300} closeDelay={100}>
                        <IconButton size="sm" variant="ghost" colorPalette="red" aria-label={t('reels.delete')} onClick={handleDelete}>
                            <LuTrash />
                        </IconButton>
                    </Tooltip>
                </HStack>
            </Box>
        </Box>
    )
}

export default function ReelsPage() {
    const { eventId } = useParams()
    const { isAdmin, isSysAdmin, user } = useAuth()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const fileInputRef = useRef(null)
    const replaceInputRef = useRef(null)

    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [selectedReel, setSelectedReel] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [shareUrl, setShareUrl] = useState(null)
    const [isShareOpen, setIsShareOpen] = useState(false)

    // The chosen file waits here while the name dialog is up; the upload only
    // starts once the name is confirmed.
    const [pendingFile, setPendingFile] = useState(null)
    const [pendingTitle, setPendingTitle] = useState('')

    const [editingReel, setEditingReel] = useState(null)
    const [editTitle, setEditTitle] = useState('')
    const [editFile, setEditFile] = useState(null)
    // null until the slider is actually dragged: absent is a different
    // answer from 0, and only the drag turns one into the other.
    const [editThumbTime, setEditThumbTime] = useState(null)
    const [saving, setSaving] = useState(false)

    const [event] = useDocumentData(doc(db, 'events', eventId))

    const reelsQuery = query(
        collection(db, 'reels'),
        where('eventId', '==', eventId),
        orderBy('createdAt', 'desc')
    )

// const querySnapshot = await getDocs(reelsQuery);
// querySnapshot.forEach((doc) => {
//   // doc.data() is never undefined for query doc snapshots
//   console.log(doc.id, " => ", doc.data());
// });


    const [reelsSnapshot, loadingReels] = useCollection(reelsQuery)
    const reels = reelsSnapshot?.docs.map(d => ({ id: d.id, ...d.data() })) || []

    // console.log("Reels page eventId: " , eventId);

    if (!isAdmin) {
        navigate('/', { replace: true })
        return null
    }

    const getVideoDuration = (file) => new Promise((resolve) => {
        const url = URL.createObjectURL(file)
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.onloadedmetadata = () => {
            URL.revokeObjectURL(url)
            resolve(video.duration || null)
        }
        video.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
        video.src = url
    })

    const handleFileChange = (e) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return
        setPendingFile(file)
        setPendingTitle(titleFromFileName(file.name))
    }

    const handleConfirmUpload = async () => {
        const file = pendingFile
        if (!file) return
        const title = pendingTitle.trim() || titleFromFileName(file.name)
        setPendingFile(null)

        setUploading(true)
        setUploadProgress(0)
        try {
            const duration = await getVideoDuration(file)
            await uploadReel(eventId, file, user.uid, title, duration, (progress) => {
                setUploadProgress(Math.round(progress * 100))
            })
            toaster.create({ title: t('reels.uploaded'), status: 'success' })
        } catch (err) {
            console.error('Upload failed:', err)
            toaster.create({ title: t('reels.uploadFailed'), description: err.message, status: 'error' })
        }
        setUploading(false)
        setUploadProgress(0)
    }

    const handleOpenEdit = (reel) => {
        setEditingReel(reel)
        setEditTitle(reel.title || titleFromFileName(reel.fileName) || '')
        setEditFile(null)
        setEditThumbTime(thumbTimeOf(reel))
    }

    const closeEdit = () => {
        if (saving) return
        setEditingReel(null)
        setEditFile(null)
        setEditThumbTime(null)
    }

    const handleReplaceFileChange = (e) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return
        setEditFile(file)
        // A replacement file renames the reel by default, the same way the
        // upload dialog does — unless the name has been edited away from the
        // one it currently carries.
        const current = editingReel?.title || titleFromFileName(editingReel?.fileName) || ''
        if (editTitle.trim() === current.trim()) setEditTitle(titleFromFileName(file.name))
    }

    const handleSaveEdit = async () => {
        const reel = editingReel
        if (!reel) return
        const title = editTitle.trim() || titleFromFileName(reel.fileName) || reel.title

        setSaving(true)
        setUploadProgress(0)
        try {
            if (editFile) {
                const duration = await getVideoDuration(editFile)
                await replaceReelFile(reel, editFile, { title, duration }, (progress) => {
                    setUploadProgress(Math.round(progress * 100))
                })
            } else {
                if (title !== (reel.title || reel.fileName)) {
                    await updateReelTitle(reel.id, title)
                }
                // Compared, not truthiness-checked: moving the slider to 0 is a
                // real change away from Cloudflare's default frame.
                if (editThumbTime !== thumbTimeOf(reel)) {
                    await updateReelThumbTime(reel.id, editThumbTime)
                }
            }
            toaster.create({ title: t('reels.saved'), status: 'success' })
            setEditingReel(null)
            setEditFile(null)
        } catch (err) {
            console.error('Reel update failed:', err)
            toaster.create({ title: t('reels.saveFailed'), description: err.message, status: 'error' })
        }
        setSaving(false)
        setUploadProgress(0)
    }

    const handleDelete = async (reelId, storagePath) => {
        try {
            await deleteReel(reelId, storagePath)
            toaster.create({ title: t('reels.deleted'), status: 'success' })
            if (selectedReel?.id === reelId) setIsModalOpen(false)
        } catch (err) {
            toaster.create({ title: t('reels.deleteFailed'), description: err.message, status: 'error' })
        }
    }

    const handleShare = (reel) => {
        if (!isStreamReady(reel)) {
            toaster.create({ title: t('reels.shareNotReady'), status: 'error' })
            return
        }
        setShareUrl(sharedReelUrl(reel.id))
        setIsShareOpen(true)
    }

    const handleCopyShareUrl = () => {
        if (!shareUrl) return
        navigator.clipboard.writeText(shareUrl)
        toaster.create({ title: t('common.linkCopied'), status: 'success' })
    }

    if (!event) return <Container py={10}><Spinner /></Container>

    return (
        <Container maxW="none" px={0} py={0}>
            <Stack gap={{ base: 4, lg: 6 }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <Box>
                        <Heading size={{ base: 'md', md: 'lg' }} srOnly={{ base: true, lg: false }}>{t('reels.titleSuffix', { name: event.name })}</Heading>
                        <Text color="fg.muted">{reels.length === 1 ? t('reels.countOne', { count: reels.length }) : t('reels.countMany', { count: reels.length })}</Text>
                    </Box>
                    <HStack gap={3} wrap="wrap">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="video/*"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        {isSysAdmin && (
                            <Button
                                size="sm"
                                variant="outline"
                                // Wider than a phone's column on one line.
                                maxW="100%"
                                h="auto"
                                minH={8}
                                whiteSpace="normal"
                                onClick={async () => {
                                    try {
                                        const res = await backfillExistingReels(eventId)
                                        toaster.create({ title: t('reels.sendExistingDone', { count: res.sent }), status: 'success' })
                                    } catch (err) {
                                        toaster.create({ title: t('reels.streamError'), description: err.message, status: 'error' })
                                    }
                                }}
                            >
                                {t('reels.sendExisting')}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            colorPalette="brand"
                            loading={uploading}
                            loadingText={t('reels.uploading')}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <LuUpload /> {t('reels.uploadReel')}
                        </Button>
                    </HStack>
                </Flex>

                {uploading && (
                    <Box>
                        <Progress.Root value={uploadProgress} size="xs">
                            <Progress.Track>
                                <Progress.Range />
                            </Progress.Track>
                        </Progress.Root>
                        <Text fontSize="xs" color="var(--mc-text-muted)" mt={1}>{t('reels.uploadingProgress', { percent: uploadProgress })}</Text>
                    </Box>
                )}

                <Separator />

                {loadingReels ? (
                    <Flex justify="center" py={10}>
                        <Spinner size="xl" />
                    </Flex>
                ) : reels.length === 0 ? (
                    <EmptyState icon={LuFilm} message={t('reels.noReels')} />
                ) : (
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap={4}>
                        {reels.map(reel => (
                            <ReelCard
                                key={reel.id}
                                reel={reel}
                                onOpenModal={(r) => { setSelectedReel(r); setIsModalOpen(true) }}
                                onDelete={handleDelete}
                                onShare={handleShare}
                                onEdit={handleOpenEdit}
                            />
                        ))}
                    </SimpleGrid>
                )}
            </Stack>

            <Dialog.Root open={isModalOpen} onOpenChange={(e) => setIsModalOpen(e.open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content maxW={{ base: '95vw', md: '800px' }} bg="black" p={0} overflow="hidden">
                            <Dialog.CloseTrigger color="white" top={2} right={2} />
                            <Dialog.Body p={0}>
                                {selectedReel && (
                                    isStreamReady(selectedReel) ? (
                                        <StreamPlayer
                                            cf={selectedReel.cf}
                                            poster={thumbnailAt(selectedReel.cf?.thumbnailUrl, selectedReel.thumbTime)}
                                            controls
                                            autoPlay
                                            maxHeight="80vh"
                                        />
                                    ) : (
                                        <video
                                            key={selectedReel.id}
                                            src={selectedReel.videoUrl}
                                            controls
                                            autoPlay
                                            style={{ width: '100%', maxHeight: '80vh', display: 'block' }}
                                        />
                                    )
                                )}
                            </Dialog.Body>
                            {selectedReel && (
                                <Box p={4} bg="bg.surface">
                                    <Flex justify="space-between" align="center">
                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium">
                                                {selectedReel.title || selectedReel.fileName}
                                            </Text>
                                            <Text fontSize="xs" color="fg.muted">
                                                {selectedReel.createdAt
                                                    ? new Date(selectedReel.createdAt.seconds * 1000).toLocaleString()
                                                    : ''}
                                            </Text>
                                        </Box>
                                        <DownloadButton
                                            size="sm"
                                            source={isStreamReady(selectedReel) ? { kind: 'reel', id: selectedReel.id } : undefined}
                                            url={selectedReel.videoUrl}
                                            fileName={selectedReel.fileName || `reel-${selectedReel.id}.mp4`}
                                        />
                                    </Flex>
                                </Box>
                            )}
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            {/* Name the reel before the upload starts. The filename is only a
                default — most reels want a human title, and the file input
                gives us nowhere to ask. */}
            <Dialog.Root
                open={!!pendingFile}
                onOpenChange={(e) => { if (!e.open) setPendingFile(null) }}
            >
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content maxW="420px">
                            <Dialog.Header>
                                <Dialog.Title>{t('reels.nameDialogTitle')}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.CloseTrigger />
                            <Dialog.Body>
                                <Field.Root>
                                    <Field.Label>{t('reels.reelName')}</Field.Label>
                                    <Input
                                        autoFocus
                                        value={pendingTitle}
                                        placeholder={t('reels.reelNamePlaceholder')}
                                        onChange={(e) => setPendingTitle(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmUpload() }}
                                    />
                                    <Field.HelperText>{pendingFile?.name}</Field.HelperText>
                                </Field.Root>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button variant="outline" onClick={() => setPendingFile(null)}>
                                    {t('common.cancel')}
                                </Button>
                                <Button colorPalette="brand" onClick={handleConfirmUpload}>
                                    <LuUpload /> {t('reels.uploadReel')}
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            {/* Edit dialog. Replacing the file keeps the reel document, so the
                share link and any QR printed against it stay valid — that is
                the whole reason this is not a delete-and-re-upload. */}
            <Dialog.Root open={!!editingReel} onOpenChange={(e) => { if (!e.open) closeEdit() }}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content maxW="480px">
                            <Dialog.Header>
                                <Dialog.Title>{t('reels.editTitle')}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.CloseTrigger disabled={saving} />
                            <Dialog.Body>
                                <Stack gap={5}>
                                    <Field.Root>
                                        <Field.Label>{t('reels.reelName')}</Field.Label>
                                        <Input
                                            autoFocus
                                            value={editTitle}
                                            placeholder={t('reels.reelNamePlaceholder')}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            disabled={saving}
                                        />
                                    </Field.Root>

                                    <Field.Root>
                                        <Field.Label>{t('reels.file')}</Field.Label>
                                        <input
                                            ref={replaceInputRef}
                                            type="file"
                                            accept="video/*"
                                            style={{ display: 'none' }}
                                            onChange={handleReplaceFileChange}
                                        />
                                        <HStack gap={3} w="100%">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={saving}
                                                onClick={() => replaceInputRef.current?.click()}
                                            >
                                                <LuUpload /> {t('reels.replaceFile')}
                                            </Button>
                                            <Text fontSize="xs" color="fg.muted" truncate>
                                                {editFile
                                                    ? t('reels.newFileSelected', { name: editFile.name })
                                                    : (editingReel?.fileName || '')}
                                            </Text>
                                        </HStack>
                                        <Field.HelperText>{t('reels.replaceFileHelp')}</Field.HelperText>
                                    </Field.Root>

                                    {/* Hidden while a replacement is staged: the
                                        frames of a file that has not been encoded
                                        yet cannot be previewed or chosen. */}
                                    {!editFile && (
                                        <ThumbnailPicker
                                            reel={editingReel}
                                            value={editThumbTime}
                                            onChange={setEditThumbTime}
                                            disabled={saving}
                                        />
                                    )}

                                    {saving && editFile && (
                                        <Box>
                                            <Progress.Root value={uploadProgress} size="xs">
                                                <Progress.Track>
                                                    <Progress.Range />
                                                </Progress.Track>
                                            </Progress.Root>
                                            <Text fontSize="xs" color="fg.muted" mt={1}>
                                                {t('reels.replacing', { percent: uploadProgress })}
                                            </Text>
                                        </Box>
                                    )}
                                </Stack>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button variant="outline" onClick={closeEdit} disabled={saving}>
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    colorPalette="brand"
                                    onClick={handleSaveEdit}
                                    loading={saving}
                                    loadingText={t('common.saving')}
                                >
                                    {t('reels.save')}
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            {/* Share dialog */}
            <Dialog.Root open={isShareOpen} onOpenChange={(e) => setIsShareOpen(e.open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content maxW="400px">
                            <Dialog.Header>
                                <Dialog.Title>{t('reels.shareTitle')}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.CloseTrigger />
                            <Dialog.Body>
                                {shareUrl && (
                                    <Stack gap={4} align="center">
                                        <Box bg="white" p={4} borderRadius="lg">
                                            <QRCodeCanvas value={shareUrl} size={200} />
                                        </Box>
                                        <Box
                                            w="100%"
                                            p={3}
                                            bg="var(--mc-bg-raised)"
                                            border="1px solid"
                                            borderColor="var(--mc-border)"
                                            borderRadius="md"
                                            cursor="pointer"
                                            onClick={handleCopyShareUrl}
                                            _hover={{ borderColor: 'var(--mc-border-strong)' }}
                                        >
                                            <Text
                                                fontSize="xs"
                                                fontFamily="'JetBrains Mono', monospace"
                                                color="var(--mc-text)"
                                                wordBreak="break-all"
                                                textAlign="center"
                                            >
                                                {shareUrl}
                                            </Text>
                                        </Box>
                                        <Button size="sm" variant="outline" onClick={handleCopyShareUrl} w="100%">
                                            {t('common.copyLink')}
                                        </Button>
                                    </Stack>
                                )}
                            </Dialog.Body>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </Container>
    )
}
